import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { 
  HelpCircle, 
  RefreshCw, 
  Calendar, 
  Clock, 
  Mail, 
  Phone, 
  MessageCircle,
  CheckCircle2,
  AlertCircle,
  ArrowLeft,
  Reply,
  Trash2
} from 'lucide-react';
import { practiceApi } from '../services/authApi';
import { useToast } from '../hooks/use-toast';

const SupportHistory = ({ onBackToDashboard }) => {
  const [supportRequests, setSupportRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const { toast } = useToast();

  const loadSupportRequests = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const requests = await practiceApi.getSupportRequests();
      setSupportRequests(requests);
    } catch (error) {
      console.error('Failed to load support requests:', error);
      toast({
        title: "Loading Failed",
        description: "Could not load support history. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadSupportRequests();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      loadSupportRequests(true);
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'America/New_York'
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/New_York'
    }) + ' EST';
  };

  const getRequestTypeDisplay = (request) => {
    const types = [];
    if (request.support) types.push('Support');
    if (request.suggestions) types.push('Suggestions');
    return types.join(' & ');
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'open':
        return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 border-yellow-200">Open</Badge>;
      case 'in-progress':
        return <Badge variant="secondary" className="bg-blue-100 text-blue-800 border-blue-200">In Progress</Badge>;
      case 'resolved':
        return <Badge variant="default" className="bg-green-100 text-green-800 border-green-200">Resolved</Badge>;
      case 'closed':
        return <Badge variant="outline">Closed</Badge>;
      default:
        return <Badge variant="secondary">Open</Badge>;
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this support request?')) {
      return;
    }

    setDeleting(requestId);
    try {
      await practiceApi.deleteSupportRequest(requestId);
      toast({
        title: "Request Deleted",
        description: "Support request has been deleted successfully.",
        variant: "default"
      });
      // Reload the requests to reflect the deletion
      loadSupportRequests(true);
    } catch (error) {
      console.error('Failed to delete support request:', error);
      toast({
        title: "Delete Failed",
        description: "Could not delete support request. Please try again.",
        variant: "destructive"
      });
    } finally {
      setDeleting(null);
    }
  };

  const handleSubmitReply = async (requestId) => {
    if (!replyText.trim() || replyText.trim().length < 10) {
      toast({
        title: "Validation Error",
        description: "Reply must be at least 10 characters long.",
        variant: "destructive"
      });
      return;
    }

    setSubmittingReply(true);
    try {
      await practiceApi.replyToSupportRequest(requestId, replyText.trim());
      toast({
        title: "Reply Sent",
        description: "Your reply has been sent successfully. The admin will be notified.",
        variant: "default"
      });
      setReplyingTo(null);
      setReplyText('');
      // Reload to show the new reply
      loadSupportRequests(true);
    } catch (error) {
      console.error('Failed to send reply:', error);
      toast({
        title: "Reply Failed",
        description: error.response?.data?.detail || "Could not send reply. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSubmittingReply(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <HelpCircle className="h-5 w-5 mr-2 text-blue-600" />
            Support History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading support history...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              size="sm"
              onClick={onBackToDashboard}
              className="flex items-center"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Dashboard
            </Button>
            <CardTitle className="flex items-center">
              <HelpCircle className="h-5 w-5 mr-2 text-blue-600" />
              Support History
            </CardTitle>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-xs text-gray-500">Auto-refreshes every 30s</span>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => loadSupportRequests(true)}
              disabled={refreshing}
              className="flex items-center"
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        {supportRequests.length === 0 ? (
          <div className="text-center py-8">
            <HelpCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Support Requests</h3>
            <p className="text-gray-600">
              You haven't submitted any support requests yet. Use the "Get Help" button to contact our support team.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {supportRequests.map((request) => (
              <div 
                key={request.id} 
                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <MessageCircle className="h-5 w-5 text-blue-600" />
                    <h4 className="font-medium text-gray-900">
                      {getRequestTypeDisplay(request)} Request
                    </h4>
                    {getStatusBadge(request.status)}
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="text-xs text-gray-500">
                      ID: {request.id.slice(-8)}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteRequest(request.id)}
                      disabled={deleting === request.id}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      {deleting === request.id ? (
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-600"></div>
                      ) : (
                        <Trash2 className="h-3 w-3" />
                      )}
                    </Button>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Calendar className="h-4 w-4" />
                    <span>{formatDate(request.created_at)}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Clock className="h-4 w-4" />
                    <span>{formatTime(request.created_at)}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Mail className="h-4 w-4" />
                    <span>{request.email}</span>
                  </div>
                  {request.phone && (
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <Phone className="h-4 w-4" />
                      <span>{request.phone}</span>
                    </div>
                  )}
                </div>
                
                <div className="bg-gray-50 rounded-lg p-3">
                  <h5 className="text-sm font-medium text-gray-700 mb-2">Your Request:</h5>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">
                    {request.description}
                  </p>
                </div>
                
                {/* Admin Response */}
                {request.response && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
                    <h5 className="text-sm font-medium text-blue-800 mb-2 flex items-center">
                      <Reply className="h-4 w-4 mr-1" />
                      Our Response - {new Date(request.responded_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long', 
                        day: 'numeric',
                        timeZone: 'America/New_York'
                      })} EST
                    </h5>
                    <p className="text-sm text-blue-700 whitespace-pre-wrap">
                      {request.response}
                    </p>
                  </div>
                )}
                
                {/* Display existing replies */}
                {request.replies && request.replies.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {request.replies.map((reply, index) => (
                      <div 
                        key={index}
                        className={reply.from === 'practice' 
                          ? "bg-green-50 border border-green-200 rounded-lg p-3" 
                          : "bg-blue-50 border border-blue-200 rounded-lg p-3"
                        }
                      >
                        <h5 className={`text-sm font-medium mb-2 flex items-center ${
                          reply.from === 'practice' ? 'text-green-800' : 'text-blue-800'
                        }`}>
                          <MessageCircle className="h-4 w-4 mr-1" />
                          {reply.from === 'practice' ? 'Your Reply' : 'Admin Reply'} - {new Date(reply.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long', 
                            day: 'numeric',
                            timeZone: 'America/New_York'
                          })} EST
                        </h5>
                        <p className={`text-sm whitespace-pre-wrap ${
                          reply.from === 'practice' ? 'text-green-700' : 'text-blue-700'
                        }`}>
                          {reply.text}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Reply button and form */}
                {request.response && replyingTo !== request.id && (
                  <div className="mt-3">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-blue-600 border-blue-300 hover:bg-blue-50"
                      onClick={() => setReplyingTo(request.id)}
                    >
                      <Reply className="h-4 w-4 mr-1" />
                      Reply to Admin
                    </Button>
                  </div>
                )}
                
                {replyingTo === request.id && (
                  <div className="mt-3 bg-gray-50 border border-gray-300 rounded-lg p-3">
                    <h5 className="text-sm font-medium text-gray-800 mb-2">Your Reply:</h5>
                    <Textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Type your reply here (minimum 10 characters)..."
                      rows={4}
                      className="mb-2"
                    />
                    <div className="flex items-center space-x-2">
                      <Button
                        size="sm"
                        onClick={() => handleSubmitReply(request.id)}
                        disabled={submittingReply || replyText.trim().length < 10}
                      >
                        {submittingReply ? 'Sending...' : 'Send Reply'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setReplyingTo(null);
                          setReplyText('');
                        }}
                        disabled={submittingReply}
                      >
                        Cancel
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      The admin will receive an email notification when you send your reply.
                    </p>
                  </div>
                )}
                
                {!request.response && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-3">
                    <p className="text-sm text-yellow-800 flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      We're reviewing your request and will respond within 24 hours.
                    </p>
                  </div>
                )}
                
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {request.support && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-red-100 text-red-800">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        Support
                      </span>
                    )}
                    {request.suggestions && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        Suggestions
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-gray-500">
                    Submitted {new Date(request.created_at).toLocaleDateString('en-US', {
                      timeZone: 'America/New_York'
                    })} EST
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SupportHistory;
